import { Card, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import CloseIcon from '@material-ui/icons/RemoveCircleOutline';
import * as React from 'react';
import axios from 'axios';
import { useCallback, useState, useEffect, useRef } from 'react';
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Button,
  Create,
  DateInput,
  FormDataConsumer,
  Loading,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  useDataProvider,
  useNotify
} from 'react-admin';
import { useForm } from 'react-final-form';
import { Toolbar } from 'react-admin';
import { StrataDestinationListInput } from '../../components';
import { alphanumeric, requiredNonEmpty } from '../../helpers';
import { createUpdateStyle } from '../../style';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  EstimateIdInput,
  IsciInput,
  OriginalIsciInput,
  SlateTypeInput,
} from '../../components/BrsFormComponents';
import { STRATA_MARK_PROCESSED } from 'src/constants';
import { useLocation } from 'react-router-dom';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { buttonStyle, EditWithToolbarStyle } from '../../style';
import PDFPreviewModal from '../../components/PDF/PDFPreviewModal';

export const BrsCreateStrata = (props) => {
  const location = useLocation();
  const notify = useNotify();
  const classes = useStyles();
  const buttonClasses = buttonStyle();
  const editClasses = EditWithToolbarStyle();
  const dataProvider = useDataProvider();
  const datePickerRef = useRef(null);

  const [validatingIsci, setValidatingIsci] = useState(true);
  const [validatingOriginalIsci, setValidatingOriginalIsci] = useState(true);
  const [focusedInput, setFocusedInput] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [originalIscisDisabled, setOriginalIscisDisabled] = useState({});
  const [unitRotation, setUnitRotation] = useState('unit');
  const [includeSD, setIncludeSD] = useState({});
  const [focusedInputs, setFocusedInputs] = useState({});
  const [validDestinationUUIDs, setValidDestinationUUIDs] = useState([]);

  const [estimateData, setEstimateData] = useState({
    selectedEstimateId: null,
    selectedDestinations: [],
    selectedUUIDs: {}
  });

  useEffect(() => {
    // Try multiple sources for state
    const stateFromProps = props.location?.state;
    const stateFromLocation = location.state;
    // Persist to local storage as a backup
    const storedData = JSON.parse(localStorage.getItem('estimateData') || '{}');

    const finalData = storedData || { selectedEstimateId: null, selectedDestinations: [], selectedUUIDs: {} };

    setEstimateData(finalData);
    // Optionally store in localStorage for persistence
    localStorage.setItem('estimateData', JSON.stringify(finalData));
  }, [props.location, location.state]);

  const handleIncludeSDChange = useCallback(
    (field) => async (value) => {
      setIncludeSD({ ...includeSD, [field]: value });
    },
    [includeSD],
  );

  const handleValidUUIDs = (validUUIDs) => {
    setValidDestinationUUIDs(validUUIDs);
  };

  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [brsJobData, setBrsJobData] = useState(null);

  // States for the PDF preview modal
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [trafficInstructionsKey, setTrafficInstructionsKey] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (data.length === 0 && props.match.params.id) {
      setLoading(false);
      dataProvider.getOne('brsjob', { id: props.match.params.id }).then(({ data }) => {
        setBrsJobData(data);
        dataProvider
          .getList('broadcastrequest', {
            pagination: { page: 1, perPage: 10000 },
            sort: { field: 'isciasset_id', order: 'ASC' },
            filter: {
              distinct_on: 'isciasset_id',
              'brsjob#request_id': data.request_id,
            },
          })
          .then(({ data }) => {
            setLoading(false);
            setData(data);
            return data;
          });
      });
    }
  }, []);

  const validateOriginalIscis = (data) => {
    if (data?.isciasset?.original_iscis?.length === 0) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && Object.values(focusedInputs).some(input => input !== null)) {
        event.preventDefault();
        event.stopPropagation();
        setFocusedInputs({});
      }
    };
    document.addEventListener('keydown', handleEsc, true);
    return () => {
      document.removeEventListener('keydown', handleEsc, true);
    };
  }, [focusedInputs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (Object.values(focusedInputs).some(input => input !== null)) {
        const portal = document.getElementById('daterangepicker-portal');
        if (!portal || !portal.contains(event.target)) {
          const isCalendarClick = event.target.closest('.DayPicker') ||
                                event.target.closest('.CalendarDay') ||
                                event.target.closest('.DayPickerNavigation') ||
                                event.target.closest('.CalendarMonth') ||
                                event.target.closest('.DateRangePicker');

          if (!isCalendarClick) {
            setFocusedInputs({});
          }
        }
      }
    };

    if (Object.values(focusedInputs).some(input => input !== null)) {
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [focusedInputs]);

  const markAsProcessed = async () => {
    const strataMarkProcessedUrl = `${window.REACT_APP_API_URL}${STRATA_MARK_PROCESSED}`;
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(strataMarkProcessedUrl, {
        order_ids: validDestinationUUIDs
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      notify('Error Marking as processed', { type: 'error' });
    }
  };

  // Handler for the review button click
  const handleReviewClick = async (formValues) => {
    try {

      setIsSubmitting(true);
      setFormData(formValues);

      // Call the generate-traffic-instructions endpoint
      const token = localStorage.getItem('token');

      // Wrap formValues in a brs_job object as requested
      const payload = { brs_job: formValues };


      const response = await axios.post(
        `${window.REACT_APP_API_URL}/api/v1/generate-traffic-instructions`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        // Use only the URL field for all environments
        if (response.data.url) {
          setPdfUrl(response.data.url);
        } else {
          notify('No PDF URL received', 'error');
          return;
        }

        // Store both key and filename from response
        setTrafficInstructionsKey(response.data.key);
        setPdfFileName(response.data.filename);
        setPdfModalOpen(true);
        localStorage.setItem('estimateData', JSON.stringify(estimateData));
      } else {
        notify('Failed to generate traffic instructions', 'error');
      }
    } catch (error) {
      notify(`Error generating traffic instructions: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for the send button click in the modal
  const handleSendBrsCreate = async (pdfFormData) => {
    try {
      setIsSubmitting(true);

      let dataToSend;
      let payload;


      // If pdfFormData is provided, we're using the PDF attachment workflow
      if (pdfFormData && pdfFormData.pdfFile) {
        // First, upload the PDF file directly to the server
        const formData = new FormData();
        formData.append('file', pdfFormData.pdfFile);

        const token = localStorage.getItem('token');

        // Upload the PDF file to the upload-traffic-instructions endpoint
        const uploadResponse = await axios.post(
          `${window.REACT_APP_API_URL}/upload-traffic-instructions`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // Extract the key from the upload response
        const uploadedFileKey = uploadResponse.data.key;
        const uploadedFileName = pdfFormData.pdfFile.name;

        // Make sure we include brs_spots from the form data if available
        dataToSend = {
          ...pdfFormData,
          trafficinstructions_key: uploadedFileKey,
          file_name: uploadedFileName,
          filepath: uploadedFileKey,
          // Ensure brs_spots exists even in PDF workflow
          brs_spots: pdfFormData.brs_spots || [{}]
        };

        // Remove the actual file object before sending to API
        const { pdfFile, ...dataWithoutFile } = dataToSend;
        payload = { brs_job: dataWithoutFile };
      } else if (trafficInstructionsKey && pdfFileName) {
        // Regular workflow with generated PDF from handleReviewClick
        dataToSend = {
          ...formData,
          trafficinstructions_key: trafficInstructionsKey,
          file_name: pdfFileName,
          filepath: trafficInstructionsKey
        };
        payload = { brs_job: dataToSend };
      } else {
        // No PDF attached and no PDF generated via review - use form data directly
        // Make empty strings for the PDF fields
        dataToSend = {
          ...pdfFormData,
          trafficinstructions_key: '',
          file_name: '',
          filepath: ''
        };

        // Remove pdfFile property if it exists but is null
        const { pdfFile, ...dataWithoutFile } = dataToSend;
        payload = { brs_job: dataWithoutFile };
      }

      // Call the brs-create endpoint
      const token = localStorage.getItem('token');
      await axios.post(
        `${window.REACT_APP_API_URL}/api/v1/brs-create`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Close the modal and notify success
      setPdfModalOpen(false);
      notify('BRS job created successfully', 'success');

      // Call markAsProcessed to finalize the process
      await markAsProcessed();

      // Redirect to the BRS list page
      window.location.href = '#/brs';
    } catch (error) {
      notify(`Error creating BRS job: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for closing the modal
  const handleCloseModal = () => {
    setPdfModalOpen(false);
  };

  //if (!brsJobData) return <Loading />;
  if (estimateData?.selectedEstimateId === null || estimateData?.selectedDestinations?.length === 0) return <Loading />;
  return (
    <div className={classes.root}>
      <a className={classes.breadcrumbLink} href="#/brs">
        {'< BRS Jobs'}
      </a>
      <Create
        title="Add BRS Job"
        resource="brsjob"
        basePath={'/brs'}
        {...props}
        style={{ overflow: 'visible', position: 'static' }}
      >
        <SimpleForm
          redirect="list"
          toolbar={<ReviewToolbar
            {...props}
            tiUpdate={false}
            disabled={disabled}
            onReview={handleReviewClick}
            onSend={handleSendBrsCreate}
          />}
          validateOnBlur={true}>
          <Typography variant="h6">New Request</Typography>
          <Card style={{ display: 'flex', width: '100%' }}>
            <TextInput
              validate={[required(), requiredNonEmpty]}
              className={classes.textField}
              source="request_id"
              label="Request ID"
              style={{ marginRight: '2rem', width: '25%' }}
            />
            <EstimateIdInput
              validate={[required(), requiredNonEmpty]}
              className={classes.textField}
              source="estimate_id"
              label="Estimate ID"
              initialEstimateId={estimateData.selectedEstimateId}
              style={{ marginRight: '2rem', width: '25%' }}
              //disabled={true}
            />
            <SlateTypeInput
              source="slate_type"
              validate={[required()]}
              choices={[
                { id: 'CADENT', name: 'Cadent' },
                { id: 'CROSS', name: 'Cross' },
                {
                  id: 'FLUIDITY',
                  name: 'Fluidity',
                },
              ]}
              className={classes.textField}
              defaultValue="CADENT"
              style={{ marginRight: '2rem', width: '25%' }}
            />
            <SelectInput
              source="market_type"
              validate={[required()]}
              choices={[
                { id: 'LOCAL', name: 'Local' },
                { id: 'NATIONAL', name: 'National' },
              ]}
              className={classes.textField}
              defaultValue="LOCAL"
              style={{
                marginRight: '2rem',
                width: '25%',
              }}
            />
          </Card>
          <Card style={{ display: 'inline-flex', width: '100%' }}>
            <div style={{ marginRight: '2rem', width: '23%' }}>
              <ReferenceInput
                source="inventory_source_id"
                label="Inventory Source"
                perPage={500}
                sort={{ field: 'name', order: 'ASC' }}
                className={classes.textField}
                validate={[required()]}
                reference="inventorysource"
                filterToQuery={(searchText) => ({ name: searchText })} // Enable filtering based on the input
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </div>
            <div style={{ marginRight: '2rem', width: '23%' }}>
              <SelectInput
                source="unit_rotation"
                choices={[
                  { id: 'unit', name: 'Unit' },
                  { id: 'rotation', name: 'Rotation' },
                ]}
                onChange={(e) => setUnitRotation(e.target.value)}
                defaultValue="unit"
                label="Unit/Rotation"
                className={classes.textField}
                validate={[required()]}
              />
            </div>
          </Card>
          <Card style={{ width: '98.6%', padding: 10 }} variant="outlined">
            <ArrayInput source="brs_spots" initialValue={[{}]} label="" validate={[required()]}>
              <SimpleFormIterator
                TransitionProps={{ enter: false, exit: false }}
                variant="filled"
                addButton={
                  <Button label="ra.action.add" style={{ color: '#3CE7EA' }}>
                    <AddIcon />
                  </Button>
                }
                removeButton={
                  <Button label="ra.action.remove" style={{ color: '#e57373', marginTop: 12, marginRight: 8 }}>
                    <CloseIcon />
                  </Button>
                }>
                <FormDataConsumer>
                  {({ getSource, scopedFormData, index }) => {
                    if (unitRotation === 'rotation' && scopedFormData?.unit) {
                      scopedFormData.unit = '';
                    } else if (unitRotation === 'unit' && scopedFormData?.rotation) {
                      scopedFormData.rotation = '';
                    }
                    return (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <div className={classes.dateRangePickerContainer} ref={datePickerRef}>
                            <DateRangePicker
                              startDate={scopedFormData?.start_date ? moment(scopedFormData.start_date) : null}
                              startDateId={`start_date_${index}`}
                              endDate={scopedFormData?.end_date ? moment(scopedFormData.end_date) : null}
                              endDateId={`end_date_${index}`}
                              onDatesChange={({ startDate, endDate }) => {
                                scopedFormData.start_date = startDate ? startDate.format('YYYY-MM-DD') : null;
                                scopedFormData.end_date = endDate ? endDate.format('YYYY-MM-DD') : null;
                              }}
                              focusedInput={focusedInputs[index]}
                              onFocusChange={(focusedInput) => {
                                setFocusedInputs(prev => ({ ...prev, [index]: focusedInput }));
                              }}
                              displayFormat="YYYY-MM-DD"
                              isOutsideRange={() => false}
                              appendToBody={true}
                              openDirection="down"
                              anchorDirection="left"
                              withPortal={true}
                              portalId="daterangepicker-portal"
                              keepOpenOnDateSelect={false}
                              minimumNights={0}
                              onClose={() => setFocusedInputs(prev => ({ ...prev, [index]: null }))}
                              closeOnEscape={true}
                              noBorder={true}
                            />
                          </div>
                          <TextInput
                            source={getSource('title')}
                            record={scopedFormData}
                            label="Title"
                            style={{ marginLeft: '0', width: '20%' }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            width: '100%',
                            marginTop: '8px',
                          }}>
                        </div>
                        <div style={{ display: 'flex', width: '100%' }}>
                          <IsciInput
                            className={classes.textField}
                            validate={[required(), alphanumeric]}
                            source={getSource('isciasset.isci')}
                            record={scopedFormData}
                            label="ISCI"
                            style={{ marginRight: '2rem', width: '25%' }}
                            setValidatingIsci={setValidatingIsci}
                            setOriginalIscisDisabled={setOriginalIscisDisabled}
                            originalIscisDisabled={originalIscisDisabled}
                            originalIscisField={getSource('isciasset.isci')}
                            duration={getSource('isciasset.duration')}
                            originaliscis={getSource('isciasset.original_iscis')}
                            isciAssetSource={getSource('isciasset')}
                          />
                          <SelectInput
                            source={getSource('isciasset.sigma_encoding')}
                            choices={[
                              { id: 'NONE', name: 'None' },
                              { id: 'CADENT', name: 'Cadent' },
                              {
                                id: 'TTWN',
                                name: 'TTWN',
                              },
                            ]}
                            defaultValue="CADENT"
                            label="Sigma Encoding"
                            className={classes.textField}
                            validate={[required()]}
                            style={{ marginRight: '2rem', width: '25%' }}
                            disabled={scopedFormData && scopedFormData['isciasset']?.exists}
                          />
                          <SelectInput
                            choices={[{ id: 'NO', name: 'No' }]}
                            defaultValue="NO"
                            label="BVS Encoding"
                            className={classes.textField}
                            validate={[required()]}
                            source={getSource('isciasset.bvs_encoding')}
                            style={{ marginRight: '2rem', width: '25%' }}
                            disabled={true}
                          />
                          <NumberInput
                            className={classes.textField}
                            style={{ display: 'none' }}
                            source={getSource('isciasset.duration')}
                            record={scopedFormData}
                            helperText={false}
                            initialValue={0}
                          />
                          <TextInput
                            className={classes.textField}
                            validate={[required(), requiredNonEmpty]}
                            source={getSource(unitRotation)}
                            record={scopedFormData}
                            label={unitRotation}
                            style={{ width: '12%', minWidth: '60px' }}
                            onChange={(e) => {
                              scopedFormData[unitRotation] = e.target.value;
                            }}
                            InputProps={{
                              endAdornment:
                                unitRotation === 'rotation' ? (
                                  <InputAdornment position="start">%</InputAdornment>
                                ) : null,
                            }}
                          />
                          <BooleanInput
                            defaultValue={true}
                            className={classes.switch}
                            source={getSource('isciasset.include_sd')}
                            record={scopedFormData}
                            validate={[required()]}
                            onChange={handleIncludeSDChange(getSource('include_sd'))}
                            label="Include SD"
                            style={{
                              margin: '1rem 2rem 0rem 2rem',
                              width: '25%',
                            }}
                            disabled={scopedFormData && scopedFormData['isciasset']?.exists}
                          />
                        </div>
                        <div style={{ display: 'flex', width: '96%' }}>
                          <OriginalIsciInput
                            source={getSource('isciasset.original_iscis')}
                            validate={[validateOriginalIscis(scopedFormData)]}
                            nestedLabel="Original ISCI"
                            maxLength={4}
                            disabled={
                              originalIscisDisabled.hasOwnProperty(getSource('isciasset.isci'))
                                ? originalIscisDisabled[getSource('isciasset.isci')]
                                : true
                            }
                            setValidatingOriginalIsci={setValidatingOriginalIsci}
                            getSource={getSource}
                            durationSource={getSource('isciasset.duration')}
                            isciAssetExists={scopedFormData && scopedFormData['isciasset']?.exists}
                          />
                        </div>
                      </>
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </Card>
          <Card style={{ display: 'flex', width: '100%', margin: '10px 0' }}>
            <StrataDestinationListInput
              brsDestinations={[]}
              style={{ width: '100%' }}
              source="destinations"
              resource="destination"
              sort={{ field: 'call_letters', order: 'ASC' }}
              canDelete={true}
              isCreate={true}
              estimateData={estimateData}
              onValidUUIDs={handleValidUUIDs}
            />
          </Card>
        </SimpleForm>
      </Create>

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        open={pdfModalOpen}
        onClose={handleCloseModal}
        pdfUrl={pdfUrl}
        onSend={handleSendBrsCreate}
        isLoading={isSubmitting}
      />
    </div>
  );
};

// Custom Toolbar component for the review workflow
const ReviewToolbar = ({ onReview, disabled, onSend, ...props }) => {
  // Use the form directly passed from SimpleForm
  const form = useForm();
  const buttonClasses = buttonStyle();
  const editClasses = EditWithToolbarStyle();
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleReviewClick = () => {
    if (form) {
      // Get all form values rather than just dirty values
      const values = form.getState().values;

      // Manually validate the form
      const errors = {};
      const requiredFields = ['request_id', 'estimate_id', 'slate_type'];

      requiredFields.forEach(field => {
        if (!values[field]) {
          errors[field] = 'This field is required';
        }
      });

      if (Object.keys(errors).length === 0) {
        // Call the parent component's handler with the form values
        onReview(values);
      } else {
        form.submit(); // This will display validation errors
      }
    } else {
      console.log('Form object is not available');
    }
  };

  // Function to handle PDF attachment
  const handleAttachPDF = () => {
    // Create an invisible file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    // Trigger the file dialog
    fileInput.click();

    // Handle file selection
    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        setSelectedPdf(file);
      }
      document.body.removeChild(fileInput);
    };
  };

  // Function to handle Send button click
  const handleSend = () => {
    if (form) {
      // Check if a PDF is attached
      if (!selectedPdf && !isPdfAttached()) {
        // If no PDF is attached, open the confirmation dialog
        setConfirmDialogOpen(true);
      } else {
        // If a PDF is attached, proceed with sending
        sendForm();
      }
    }
  };

  // Function to check if a PDF is already generated
  const isPdfAttached = () => {
    return selectedPdf !== null;
  };

  // Function to actually send the form
  const sendForm = () => {
    if (form) {
      // Get all form values
      const values = form.getState().values;

      // Always pass all form values regardless of PDF attachment
      const formDataWithPdf = {
        ...values,
        pdfFile: selectedPdf
      };

      // Call the parent's handleSendBrsCreate function directly
      onSend(formDataWithPdf);

      // Close the dialog if it was open
      setConfirmDialogOpen(false);
    }
  };

  // Function to handle Cancel button click in the confirmation dialog
  const handleCancelConfirmation = () => {
    setConfirmDialogOpen(false);
  };

  // Function to remove the selected PDF
  const removePdf = () => {
    setSelectedPdf(null);
  };

  // Store if a PDF is attached in a variable for rendering purposes
  const hasPdf = selectedPdf !== null;

  return (
    <Toolbar style={{ backgroundColor: 'transparent', padding: '8px 0', margin: '16px', display: 'flex', width: '50%' }}>
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Button
            label="Send"
            onClick={handleSend}
            disabled={false}
            className={buttonClasses.button}
            style={{ marginRight: '8px' }}
          />
          <Button
            label="Generate"
            onClick={handleReviewClick}
            disabled={hasPdf}
            className={buttonClasses.button}
            style={{ marginRight: '8px' }}
          />
          <Button
            label="Cancel"
            onClick={() => window.location.href = '#/brs'}
            disabled={false}
            className={editClasses.cancelButton}
            style={{ textTransform: 'none' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {hasPdf && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#3a3a3a',
              borderRadius: '4px',
              padding: '4px 8px',
              marginRight: '10px'
            }}>
              <span style={{ marginRight: '8px' }}>{selectedPdf.name}</span>
              <CloseIcon
                style={{ cursor: 'pointer', fontSize: '18px' }}
                onClick={removePdf}
              />
            </div>
          )}
          <Button
            label="Attach PDF"
            onClick={handleAttachPDF}
            className={buttonClasses.button}
            style={{ marginRight: '25px' }}
          />
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to send this job without attaching a PDF?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={handleCancelConfirmation}
            className={editClasses.cancelButton}
            style={{ textTransform: 'none' }}
          />
          <Button
            label="Send"
            onClick={sendForm}
            className={buttonClasses.button}
            autoFocus
          />
        </DialogActions>
      </Dialog>
    </Toolbar>
  );
};

const useStyles = makeStyles((theme) => ({

  dateRangePickerContainer: {
    width: '740px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '20px',
    marginRight: '1rem',
    '& .DateRangePicker': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
    },
    '& .DateRangePickerInput': {
      display: 'flex',
      flexDirection: 'row',
      border: 'unset',
      gap: '18px',
      backgroundColor: '#424242',
      width: '100%',
    },
    '& .DateInput': {
      width: '340px',
      borderBottom: '1px solid #ffffff',
      padding: '0 0 0 3px',
      margin: '0 15px 0 0',
      backgroundColor: '#5A5A5A',
      height: '48px',
      display: 'flex',
      '&:hover': {
        backgroundColor: '#6B6B6B',
        borderColor: '#aaa',
      },
      '& .DateInput_input': {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.875rem',
        '&:focus': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '&:not(:placeholder-shown)': {
          color: '#ffffff',
        },
      },
    },
    '& .DateInput_input': {
      padding: '0',
      backgroundColor: '#5A5A5A',
      fontSize: '0.875rem',
      '&:hover': {
        backgroundColor: '#6B6B6B',
        borderColor: '#aaa',
      },
    },
    '& .DateInput_input::placeholder': {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.875rem',
    },
    '& .DateRangePickerInput_arrow': {
      display: 'none !important',
    },
  },
  textField: {
    width: '98%',
  },
  '& svg': {
    color: 'red',
  },
  link: {
    color: '#3CE7EA',
    fontSize: '14px',
    fontFamily: 'Roboto',
    fontWeight: 400,
    textDecoration: 'underline',
  },
  breadcrumbLink: {
    color: '#3CE7EA',
    fontSize: '16px',
    fontFamily: 'sans-serif, Roboto',
    fontWeight: 400,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  root: {
    width: '100%',
    marginTop: '24px',
    position: 'relative',
    height: 'calc(100vh - 100px)',
    overflowY: 'auto',
    '& .ra-input': {
      position: 'static'
    },
    '& .RaSimpleFormIterator-form': {
      position: 'static',
      overflow: 'auto',
      maxHeight: '500px',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#424242',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#5A5A5A',
        borderRadius: '4px',
        '&:hover': {
          background: '#6B6B6B',
        },
      },
    },
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#424242',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#5A5A5A',
      borderRadius: '4px',
      '&:hover': {
        background: '#6B6B6B',
      },
    },
  },
  card: {
    width: '100%',
    marginTop: '24px',
    position: 'relative',
    '& .ra-input': {
      position: 'static'
    },
    '& .RaSimpleFormIterator-form': {
      position: 'static'
    }
  },
  switch: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#3CE7EA',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#3CE7EA',
    },
  },
  statusButton: {
    borderRadius: '50%',
    backgroundColor: 'green',
    color: 'white',
    minWidth: '26px',
    padding: 0,
    height: '26px',
    '&:hover': {
      backgroundColor: 'darkgreen',
    },
  },
  modalToolbar: {
    backgroundColor: 'initial',
    justifyContent: 'end',
  },
  showButton: {
    color: '#3CE7EA',
    border: '1px solid #3CE7EA',
  },
  button: {
    backgroundColor: '#3CE7EA',
    color: '#2A2A2A',
    borderRadius: 'unset',
    padding: '4px 8px 4px 8px',
    textTransform: 'none',
    fontSize: '14px',
    opacity: 0.9,
    '&:hover': {
      backgroundColor: '#3CE7EA',
      color: '#2A2A2A',
      opacity: 1,
      boxShadow: 'none',
    },
  },
}));
